import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { NotificationCenterService } from '../services/notification-center.service'
import { toAppNotification } from '../utils/notification-center'
import type { AppNotification } from '../types/notification-center.types'

// Vue 2 loads 10; a few more keeps a busy day's jobs in view.
export const NOTIFICATIONS_TAKE = 20

/**
 * The top-bar bell's notifications (Vue 2 TopNavbar + notificationHubService). Notifications are
 * stored server-side, so this reads them back; useNotificationCenterPolling keeps it current.
 */
export const useNotificationCenterStore = defineStore('notificationCenter', () => {
	const service = new NotificationCenterService()

	const items = ref<AppNotification[]>([])
	const loaded = ref(false)
	const loading = ref(false)
	// Only a first load that failed is worth showing: later failures keep the last good list.
	const loadFailed = ref(false)

	// Marked read here but maybe not yet on the server. A refresh that left before the mark landed
	// would otherwise bring them back as unread.
	const markedRead = new Set<string>()
	let inFlight: Promise<void> | null = null

	const unreadCount = computed(() => items.value.filter((n) => n.unread).length)

	function refresh(): Promise<void> {
		inFlight ??= (async () => {
			loading.value = true
			try {
				const rows = await service.getNotifications(NOTIFICATIONS_TAKE)
				items.value = rows.map((row) => {
					const item = toAppNotification(row)
					return markedRead.has(item.id) ? { ...item, unread: false } : item
				})
				loaded.value = true
				loadFailed.value = false
			} catch {
				if (!loaded.value) loadFailed.value = true
			} finally {
				loading.value = false
				inFlight = null
			}
		})()
		return inFlight
	}

	/** Vue 2 marks everything read as the bell opens. */
	async function markAllRead(): Promise<void> {
		const ids = items.value.filter((n) => n.unread).map((n) => n.id)
		if (ids.length === 0) return

		ids.forEach((id) => markedRead.add(id))
		items.value = items.value.map((n) => (n.unread ? { ...n, unread: false } : n))
		try {
			await service.markAsRead(ids)
		} catch {
			// Still unread on the server: let the next refresh show them as such.
			ids.forEach((id) => markedRead.delete(id))
		}
	}

	function $reset(): void {
		items.value = []
		loaded.value = false
		loading.value = false
		loadFailed.value = false
		markedRead.clear()
		inFlight = null
	}

	return { items, loaded, loading, loadFailed, unreadCount, refresh, markAllRead, $reset }
})
